import { createRoute, useLoaderData } from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";
import { unwrapPageInitialData, type PageInitialData } from "~/app/api/pageData";
import { mediaLibraryPageQueryOptions } from "~/app/api/pageData/mediaLibraryPageQuery";
import type { MediaLibraryPageInitialData } from "~/app/api/pageData/mediaLibraryPageInitialData";
import { StorefrontShellFrame } from "~/app/shell/StorefrontShellFrame";
import { useStorefrontShellProps } from "~/app/router/storefrontShellPropsContext";
import { rootRoute } from "~/app/router/rootRoute";
import { createMediaLibraryPage } from "~/features/mediaLibrary/components/MediaLibraryPage";
import type { MediaFilterData, OptionItem } from "~/features/mediaLibrary/types/mediaLibraryFormTypes";

function toOptions(entries: Record<string, string> | undefined): OptionItem[] {
  if (!entries) {
    return [];
  }
  return Object.entries(entries).map(([key, label]) => ({ key, label }));
}

function toMediaFilterData(data: MediaLibraryPageInitialData | undefined): MediaFilterData {
  return {
    mediaTypes: toOptions(data?.mediaTypes),
    productPortfolios: toOptions(data?.productPortfolios),
    languages: toOptions(data?.mediaLanguages),
    segments: toOptions(data?.segments),
  };
}

function MediaLibraryRoute() {
  const result = useLoaderData({
    from: "/media-library",
  }) as PageInitialData<MediaLibraryPageInitialData>;
  const shellProps = useStorefrontShellProps();
  const filterData = toMediaFilterData(result.data);

  return (
    <StorefrontShellFrame variant={shellProps.variant} metadata={result.metadata}>
      {createMediaLibraryPage(filterData)}
    </StorefrontShellFrame>
  );
}

export async function loadMediaLibraryPageData(context: { queryClient: QueryClient }) {
  const result = await context.queryClient.ensureQueryData(mediaLibraryPageQueryOptions());

  return unwrapPageInitialData(result);
}

export const mediaLibraryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/media-library",
  loader: async ({ context }) => loadMediaLibraryPageData(context),
  component: MediaLibraryRoute,
});
